import React from "react";
import {INormalizedProduct} from "../../models/NormalizedProduct";
import {IProductLinkedItem, IProductLinkedItemEntry} from "../../models/LinkedItems";
import BestPriceDisplay from "../Products/BestPriceDisplay";
import {
    Checkbox,
    Dropdown,
    IColumn,
    IconButton,
    IDropdownOption,
    SelectionMode,
    Separator,
    ShimmeredDetailsList
} from "@fluentui/react";
import {ColumnGenerator} from "../../logic/Columns/ColumnGenerator";
import {titleCaseType} from "./linkedItemHelpers";

interface ICuratedLinkedItemsTableProps {
    type: string;
    products: INormalizedProduct[];
    entryLookup: Record<string, IProductLinkedItem>;
    typeOptions: string[];
    loading: boolean;
    canEdit: boolean;
    onSaveLink: (entry: IProductLinkedItemEntry) => void;
    onDeleteLink: (linkedProductId: string) => void;
    onViewDetails: (product: INormalizedProduct) => void;
    // Optional multi-select for bulk "List on Storefront".
    selectionEnabled?: boolean;
    selectedProductIds?: Set<string>;
    onToggleProductSelect?: (productId: string, checked: boolean) => void;
}

export const CuratedLinkedItemsTable: React.FC<ICuratedLinkedItemsTableProps> = (props) => {
    const columns: IColumn[] = [];

    if (props.selectionEnabled) {
        columns.push({
            key: 'linkedItemSelect',
            name: '',
            minWidth: 32,
            maxWidth: 32,
            onRender: (item: INormalizedProduct) => (
                <Checkbox
                    checked={props.selectedProductIds?.has(item.id) ?? false}
                    ariaLabel="Select product for storefront listing"
                    onChange={(_e, checked) => props.onToggleProductSelect?.(item.id, checked ?? false)}
                />
            )
        });
    }

    columns.push(
        ColumnGenerator('Name', 'name'),
        ColumnGenerator('Best Price', 'bestPrice', 150, 200, (item: INormalizedProduct) => <BestPriceDisplay product={item} />),
        ColumnGenerator('', 'viewDetails', 60, 60, (item: INormalizedProduct) => (
            <IconButton
                iconProps={{iconName: 'View'}}
                title="View Details"
                ariaLabel="View Details"
                onClick={() => props.onViewDetails(item)}
            />
        ))
    );

    if (props.canEdit) {
        const typeOptions: IDropdownOption[] = props.typeOptions.map(t => ({key: t, text: titleCaseType(t)}));
        // Ensure the current type is always selectable even if the suggestions list hasn't caught up.
        if (!typeOptions.some(o => o.key === props.type)) {
            typeOptions.unshift({key: props.type, text: titleCaseType(props.type)});
        }

        columns.push({
            key: 'linkedItemType',
            name: 'Type',
            minWidth: 150,
            maxWidth: 180,
            onRender: (item: INormalizedProduct) => {
                const entry = props.entryLookup[item.id];
                if (!entry) return <></>;
                return (
                    <Dropdown
                        selectedKey={entry.type}
                        options={typeOptions}
                        onChange={(_e, option) => {
                            if (!option || option.key === entry.type) return;
                            props.onSaveLink({
                                linkedProductId: entry.linkedProductId,
                                type: String(option.key),
                                recommended: entry.recommended
                            });
                        }}
                    />
                );
            }
        });
        columns.push({
            key: 'linkedItemRecommended',
            name: 'Recommended',
            minWidth: 100,
            maxWidth: 110,
            onRender: (item: INormalizedProduct) => {
                const entry = props.entryLookup[item.id];
                if (!entry) return <></>;
                return (
                    <Checkbox
                        checked={entry.recommended}
                        onChange={(_e, checked) => {
                            props.onSaveLink({
                                linkedProductId: entry.linkedProductId,
                                type: entry.type,
                                recommended: checked ?? false
                            });
                        }}
                    />
                );
            }
        });
        columns.push({
            key: 'linkedItemDelete',
            name: '',
            minWidth: 40,
            maxWidth: 40,
            onRender: (item: INormalizedProduct) => {
                const entry = props.entryLookup[item.id];
                if (!entry) return <></>;
                return (
                    <IconButton
                        iconProps={{iconName: 'Delete'}}
                        title="Remove linked item"
                        ariaLabel="Remove linked item"
                        onClick={() => props.onDeleteLink(entry.linkedProductId)}
                    />
                );
            }
        });
    }

    return (
        <div>
            <Separator>{titleCaseType(props.type)}</Separator>
            <ShimmeredDetailsList
                enableShimmer={props.loading}
                items={props.products ?? []}
                selectionMode={SelectionMode.none}
                columns={columns}
            />
        </div>
    );
};
